import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { User } from '../../types';

interface UserPageHeaderProps {
  oid: string;
  user: User;
  displayName: string;
  /** `null` when the reader has no way to know it — only self, or an admin. */
  role: string | null;
}

function roleTagClass(role: string): string {
  if (role === 'admin') {
    return 'epi-tag blue';
  }
  if (role === 'operator') {
    return 'epi-tag coral';
  }
  return 'epi-tag muted';
}

/**
 * Who the page is about, all of it, above the cards.
 *
 * This used to be a card of its own that restated the header underneath it —
 * name and UPN twice. What it keeps is what identifies the person: the number,
 * the name, the UPN, the job, and the role. Capabilities are a property of the
 * *role*, identical for every admin, so they say nothing about this person and
 * nothing about the page they head — they are a card of their own now, at the
 * end of the list, where a reader goes looking for them rather than stepping
 * over them.
 */
export function UserPageHeader({
  oid,
  user,
  displayName,
  role,
}: UserPageHeaderProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  function copyOid() {
    void navigator.clipboard.writeText(oid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <header className="mb-8">
      <div className="section-head">
        <div>
          <p className="label dash">№ {oid.slice(0, 8).toUpperCase()}</p>
          <h1 className="display mt-2 text-3xl wrap-break-word md:text-5xl">
            {displayName.toUpperCase()}
          </h1>
          <p className="mono mt-3 text-sm text-muted">
            {user.userPrincipalName}
          </p>
          {/* The full OID and the role belong to the same statement as the
              name above them — they used to sit on their own row under the
              rule, which read as a second, nameless block and cost the page
              40px before the first card. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            <button
              type="button"
              onClick={copyOid}
              className="mono text-left text-xs break-all text-muted transition-opacity hover:opacity-70"
              title={t('profile.identity.copyOid')}
            >
              {oid}
              <span
                className="ml-2 text-[10px] tracking-widest uppercase"
                style={{ color: copied ? 'var(--green)' : 'var(--muted-soft)' }}
              >
                {copied
                  ? t('profile.identity.copied')
                  : t('profile.identity.copyOid')}
              </span>
            </button>

            {role && (
              <span className={roleTagClass(role)}>
                {t(`profile.identity.roles.${role}`, { defaultValue: role })}
              </span>
            )}
          </div>
        </div>
        {user.jobTitle && (
          <div className="text-right">
            <span className="display inline-block bg-green px-3 py-1 text-base text-ink">
              {user.jobTitle}
            </span>
            {user.department && <p className="label mt-2">{user.department}</p>}
          </div>
        )}
      </div>
    </header>
  );
}
